/**
 * Which Content Review dashboard tabs a person can actually USE.
 *
 * Fredrik, 2026-08-24, walking as the producer: "The producer sees the review
 * queue on their dashboard, but since they don't have access to content review
 * there is nothing there."
 *
 * Two tabs rendered for everyone and 403'd for a producer:
 *
 *   Review Queue — `GET /cycles` is reviewer / DM / mentioned-participant only.
 *     A pure producer got "Content review access required" into the dashboard's
 *     error banner on every load, plus a tab that could never hold anything.
 *   Archive      — was the same reviewer-or-DM gate (a second dead tab nobody
 *     had noticed). Re-opened 2026-09-02 (Fredrik): the archive is the central
 *     search surface for the content hub, so archive READS are now open to the
 *     whole content_review module and the tab shows for producers/managers too.
 *     Deletion stays archive_admin.
 *
 * A producer's home is My Submissions (Fredrik's call, 2026-08-21, when the
 * queue was deliberately left closed to them). Showing them a queue they cannot
 * read is the same defect as the hand-over button they could not reach: the UI
 * describing a path that is not theirs.
 *
 * Kept out of ReviewDashboard so the rule is one testable thing rather than
 * four conditions spread through a long render.
 */

export type DashboardTab = 'queue' | 'my-submissions' | 'archive' | 'contexts' | 'taxonomy';

export interface TabCapabilities {
  canReviewContent: boolean;
  canMakeDecisions: boolean;
  canManageSubmissions: boolean;
  canSubmitContent: boolean;
  /** Mentioned on a cycle (D69). The backend scopes GET /cycles to those, so
   *  the queue IS readable — it is their only way in. */
  isParticipant: boolean;
}

export function canSeeTab(tab: DashboardTab, c: TabCapabilities): boolean {
  switch (tab) {
    case 'queue':
      return c.canReviewContent || c.canMakeDecisions || c.isParticipant;
    case 'archive':
      // 2026-09-02 — everyone with content review access browses the archive;
      // the backend gate is module-wide (requireContentReviewMember).
      return c.canReviewContent || c.canMakeDecisions || c.canSubmitContent || c.canManageSubmissions;
    case 'my-submissions':
      return c.canSubmitContent;
    case 'contexts':
      return c.canMakeDecisions;
    case 'taxonomy':
      return c.canMakeDecisions || c.canManageSubmissions;
    default:
      return false;
  }
}

/** Tab order as rendered, so "first usable" means first on screen. */
export const TAB_ORDER: DashboardTab[] = [
  'queue', 'my-submissions', 'archive', 'contexts', 'taxonomy'
];

/**
 * Where to land on open. 'queue' would put a producer on an empty list behind a
 * 403 before they had clicked anything.
 */
export function defaultTab(c: TabCapabilities): DashboardTab {
  return TAB_ORDER.find(t => canSeeTab(t, c)) || 'queue';
}

/**
 * What to SHOW, as opposed to what state happens to hold. The dashboard's
 * render chain ends in the queue as its fallthrough, so an unusable value would
 * render the one body the caller cannot read.
 */
export function resolveTab(active: DashboardTab, c: TabCapabilities): DashboardTab {
  return canSeeTab(active, c) ? active : defaultTab(c);
}
